// src/utils/prescription.js
//
// Pure logic for the prescription builder (no React, no network). Rules mirror
// the visit-workflow contract (docs/visit-workflow-contract.md, "Prescription").

export const MAX_ITEMS = 20;
export const MIN_ITEMS = 1;
export const DURATION_MIN = 1;
export const DURATION_MAX = 365;
export const DRUG_NAME_MAX = 120;
export const INSTRUCTIONS_MAX = 200;
export const DRUG_SEARCH_MIN_CHARS = 2;

/** Contract regex: 3 or 4 dose slots, each a digit with an optional ".5". */
export const FREQUENCY_REGEX = /^\d(\.5)?-\d(\.5)?-\d(\.5)?(-\d(\.5)?)?$/;

export const FREQUENCY_PRESETS = ['1-0-0', '0-0-1', '1-0-1', '1-1-1'];

/** Contract `plainTiming` table. Anything else has no plain-language form. */
const PLAIN_TIMING = {
  '1-0-0': 'Morning',
  '0-0-1': 'Night',
  '1-0-1': 'Morning and night',
  '1-1-1': 'Morning, afternoon and night',
};

const SLOT_NAMES = {
  3: ['morning', 'afternoon', 'night'],
  4: ['morning', 'afternoon', 'evening', 'night'],
};

/**
 * Plain-language reading of a frequency the contract table does not cover,
 * e.g. "0.5-0-1" -> "Morning 0.5, night 1"; null when the pattern is invalid.
 * (Preset patterns keep the contract's own plainTiming wording.)
 */
export function describeFrequency(frequency) {
  if (!isValidFrequency(frequency)) return null;
  const slots = frequency.trim().split('-');
  const parts = slots
    .map((n, i) => (Number(n) > 0 ? `${SLOT_NAMES[slots.length][i]} ${n}` : null))
    .filter(Boolean);
  if (!parts.length) return 'No doses';
  const text = parts.join(', ');
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** The contract's plainTiming where it has one, else describeFrequency. */
export const frequencyText = frequency => plainTiming(frequency) ?? describeFrequency(frequency);

export const isValidFrequency = f => typeof f === 'string' && FREQUENCY_REGEX.test(f.trim());

export function plainTiming(frequency) {
  if (typeof frequency !== 'string') return null;
  return PLAIN_TIMING[frequency.trim()] ?? null;
}

export function isValidDuration(d) {
  const n = typeof d === 'string' && d.trim() !== '' ? Number(d) : d;
  return Number.isInteger(n) && n >= DURATION_MIN && n <= DURATION_MAX;
}

export const shouldSearchDrugs = q => typeof q === 'string' && q.trim().length >= DRUG_SEARCH_MIN_CHARS;

let _key = 0;
/** A blank medicine row. `key` is a client-only React key, never sent. */
export function emptyItem(overrides = {}) {
  _key += 1;
  return {
    key: `row-${_key}`,
    drugName: '', strength: '', form: '', dose: '1 tablet',
    frequency: '1-0-1', durationDays: 5, instructions: '',
    ...overrides,
  };
}

/** A row from a `drug_catalog` hit `{id, name, strength, form, genericName}`. */
export function itemFromDrug(drug) {
  const form = (drug.form || '').toLowerCase();
  return emptyItem({
    drugName: drug.name || '',
    strength: drug.strength || '',
    form: drug.form || '',
    dose: form ? `1 ${form}` : '1 tablet',
  });
}

/** Rows from a server `Prescription.items`. */
export const itemsFromPrescription = rx =>
  (rx?.items || [])
    .slice()
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
    .map(i => emptyItem({
      drugName: i.drugName || '', strength: i.strength || '', form: i.form || '',
      dose: i.dose || '', frequency: i.frequency || '',
      durationDays: i.durationDays ?? '', instructions: i.instructions || '',
    }));

/** @returns {Object<string,string>} field → message; empty object when valid. */
export function validateItem(item) {
  const e = {};
  const name = (item.drugName || '').trim();
  if (!name) e.drugName = 'Choose or type a medicine';
  else if (name.length > DRUG_NAME_MAX) e.drugName = `Keep the name under ${DRUG_NAME_MAX} characters`;
  if (!(item.dose || '').trim()) e.dose = 'Add a dose, e.g. 1 tablet';
  if (!isValidFrequency(item.frequency || '')) e.frequency = 'Use a pattern like 1-0-1 (slots may use .5)';
  if (!isValidDuration(item.durationDays)) e.durationDays = `${DURATION_MIN}–${DURATION_MAX} days`;
  if ((item.instructions || '').length > INSTRUCTIONS_MAX) e.instructions = `Keep under ${INSTRUCTIONS_MAX} characters`;
  return e;
}

/**
 * @returns {{ok: boolean, formError: string|null, itemErrors: Object[]}}
 * An empty list is "not ok" because the contract demands 1–20 items.
 */
export function validateItems(items) {
  const itemErrors = items.map(validateItem);
  let formError = null;
  if (items.length < MIN_ITEMS) formError = 'Add at least one medicine';
  else if (items.length > MAX_ITEMS) formError = `A prescription can have at most ${MAX_ITEMS} medicines`;
  const ok = !formError && itemErrors.every(e => Object.keys(e).length === 0);
  return { ok, formError, itemErrors };
}

/** Contract `ItemInput` from a row; blank optionals are omitted. */
export function toItemInput(item) {
  const out = {
    drugName: item.drugName.trim(),
    dose: item.dose.trim(),
    frequency: item.frequency.trim(),
    durationDays: Number(item.durationDays),
  };
  if ((item.strength || '').trim()) out.strength = item.strength.trim();
  if ((item.form || '').trim()) out.form = item.form.trim();
  if ((item.instructions || '').trim()) out.instructions = item.instructions.trim();
  return out;
}

/** Body for POST/PUT /doctor/prescriptions. */
export function buildPrescriptionBody({ patientId, visitId, diagnosisText, advice, items }) {
  const body = { patientId, items: items.map(toItemInput) };
  if (visitId) body.visitId = visitId;
  if ((diagnosisText || '').trim()) body.diagnosisText = diagnosisText.trim();
  if ((advice || '').trim()) body.advice = advice.trim();
  return body;
}

/** Move a row; returns a new array (out-of-range moves are no-ops). */
export function moveItem(items, from, to) {
  if (from === to || from < 0 || to < 0 || from >= items.length || to >= items.length) return items;
  const next = items.slice();
  const [m] = next.splice(from, 1);
  next.splice(to, 0, m);
  return next;
}

/**
 * Non-blank drug names for POST /doctor/prescriptions/check, one per row. NOT
 * de-duplicated: the server reports duplicates, so collapsing "Paracetamol" +
 * "Paracetamol" here would make the duplicate warning unreachable.
 */
export const checkableDrugNames = items =>
  items.map(i => (i.drugName || '').trim()).filter(Boolean);

/** Stable key so the debounced advisory check only fires when names change. */
export const drugNamesKey = items =>
  checkableDrugNames(items).map(n => n.toLowerCase()).sort().join('|');

/** One line per row for the on-screen preview, e.g. "Paracetamol 500 mg - 1 tablet, 1-0-1 (Morning and night) for 5 days". */
export function describeItem(item) {
  const timing = frequencyText(item.frequency);
  const name = [item.drugName, item.strength].filter(Boolean).join(' ');
  return `${name} — ${item.dose}, ${item.frequency}${timing ? ` (${timing})` : ''} for ${item.durationDays} day${Number(item.durationDays) === 1 ? '' : 's'}`;
}
